import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Checkbox, CheckboxProps, FormControlLabel, Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import { OAUTH_SCOPE_OPTIONS } from 'src/constants/oauth/constants'
import type { OAuthScope } from 'src/constants/oauth/enums'
import { toggleScope } from 'src/utils/oauthClient'
import type { OAuthClientFormValues } from './OAuthClientForm.interface'

/**
 * A feature-local checklist rather than the shared `FormikCheckboxSelect`: that component has
 * no callers anywhere in this app, its Formik array wiring is unexercised, and it renders no
 * per-option description — which is the whole point of this list. The Retool tab showed raw
 * scope strings; saying what each grant allows is this tab's main improvement.
 */
interface Props {
  // Read-only without `oauth_clients_manage`.
  disabled: boolean
}

const OAuthScopesFieldComponent = ({ disabled }: Props) => {
  const { values, touched, errors, setFieldValue, setFieldTouched } =
    useFormikContext<OAuthClientFormValues>()

  const handleToggle = useCallback(
    (scope: OAuthScope, isChecked: boolean) => {
      setFieldValue('scopes', toggleScope(values.scopes, scope, isChecked))
      // `false` = do not revalidate. Formik's `setFieldTouched` validates against the values in
      // its own closure, which are still the pre-toggle ones inside this batch — so letting it
      // validate overwrites the correct result from `setFieldValue` with a stale one, and
      // checking the first box leaves "Select at least one scope" on screen.
      setFieldTouched('scopes', true, false)
    },
    [values.scopes, setFieldValue, setFieldTouched],
  )

  const error = touched.scopes && typeof errors.scopes === 'string' ? errors.scopes : null

  return (
    <div>
      <Typography variant="h6" css={tw`mb-1 text-shades-400`}>
        Access scopes
      </Typography>
      <div css={tw`flex flex-col gap-2`}>
        {OAUTH_SCOPE_OPTIONS.map((option) => {
          const isChecked = values.scopes.includes(option.value)
          // Widened exactly as BasicCheckbox.tsx does it: MUI types `inputProps` as
          // InputHTMLAttributes, which admits no data-* keys. Declaring the widened type here
          // and passing a variable (not a fresh literal) is what satisfies the excess-property
          // check — the same mechanism the shared component relies on.
          const scopeInputProps: CheckboxProps['inputProps'] & { 'data-testid'?: string } = {
            'data-testid': `oauth-scope-${option.value}`,
          }

          return (
            <FormControlLabel
              key={option.value}
              disabled={disabled}
              css={[
                tw`m-0 items-start rounded-lg border border-solid p-3`,
                isChecked ? tw`border-primary-700 bg-primary-25` : tw`border-shades-100`,
              ]}
              control={
                <Checkbox
                  checked={isChecked}
                  disabled={disabled}
                  onChange={(event) => {
                    handleToggle(option.value, event.target.checked)
                  }}
                  inputProps={scopeInputProps}
                />
              }
              label={
                <div css={tw`py-1`}>
                  <Typography variant="h6" css={tw`text-shades-1000`}>
                    {option.label}
                  </Typography>
                  <Typography variant="body2" css={tw`text-shades-500`}>
                    {option.description}
                  </Typography>
                  <Typography variant="subtitle2" css={tw`mt-1 text-shades-400`}>
                    {option.value}
                  </Typography>
                </div>
              }
            />
          )
        })}
      </div>
      <FormErrorMessage error={error} css={tw`mb-0 mt-2 text-left`} />
    </div>
  )
}

export const OAuthScopesField = memo(OAuthScopesFieldComponent)
